<template>
    <app-content class="operations-history">
        <b-container fluid class="operations-history">
        <b-row>
        <b-col>
            <reports-filter
              is-company-field-required
              is-installation-field-required
              is-asset-field-required
              :filter.sync="reportsFilter"
              :filter-objects.sync="reportsFilterObjects"
            />
        </b-col>
        </b-row>
        <b-row>
        <b-col>
            <tag-options-input
            class="mb-4"
            :label="$t('reports.operations-history.filter.operation-type')"
            :tag-label="'name'"
            :track-tag-by="'id'"
            :tags="operationTypes"
            :selected-items.sync="reportsFilter.operationTypes"
            :options-custom-label="$t('reports.operations-history.filter.all-operations-types')"
            />
        </b-col>
        <b-col>
            <tag-options-input
            v-if="haveUsers"
            class="mb-4"
            :label="$t('reports.operations-history.filter.optional-user')"
            :tag-label="'name'"
            :track-tag-by="'id'"
            :tags="users"
            :loading="loadingUsers"
            :selected-items.sync="reportsFilter.usersIds"
            :options-custom-label="$t('reports.operations-history.filter.all-users')"
            />
        </b-col>
        </b-row>
        <b-row class="mb-5">
        <b-col>
            <period-date-select
            class="operations-history__period-select"
            :initialDate.sync="reportsFilter.initialDate"
            :initialTime.sync="reportsFilter.initialTime"
            :finalDate.sync="reportsFilter.finalDate"
            :finalTime.sync="reportsFilter.finalTime"
            :period.sync="reportsFilter.period"
            :invalid.sync="invalidReportRange"
            @update:initialDate="validatePeriodFields($event, 'initialDate')"
            @update:initialTime="validatePeriodFields($event, 'initialTime')"
            @update:finalDate="validatePeriodFields($event, 'finalDate')"
            @update:finalTime="validatePeriodFields($event, 'finalTime')"
            @update:initialDateTime="onUpdateInitialDateTime($event)"
            @update:finalDateTime="onUpdateFinalDateTime($event)"
            />
        </b-col>
        </b-row>
        <b-row>
        <b-col>
          <report-footer-buttons
            :saveTemplateDisabled="saveTemplateLoading
              || emptyFields
              || invalidReportPeriod
              || !isPeriodRangeValid"
            :viewReportDisabled="saveTemplateLoading
              || emptyFields
              || invalidReportPeriod
              || !isPeriodRangeValid"
            :exportXlsxDisabled="exportXlsxLoading
              || emptyFields
              || invalidReportPeriod
              || !isPeriodRangeValid"
            :saveTemplateLoading="saveTemplateLoading"
            :exportXlsxLoading="exportXlsxLoading"
            @cancel="cancel"
            @save-template="openModal"
            @view-report="openReport"
            @export-xlsx="exportXlsx"
          />
        </b-col>
        </b-row>
    </b-container>
    <save-template-modal
      v-model="showModal"
      :name.sync="name"
      :is-loading="saveTemplateLoading"
      @input="onTemplateModalInput"
      @save="saveHandler"
    />
  </app-content>
</template>
<script>
import uniq from 'lodash/uniq';
import PeriodDateSelect from '@/components/PeriodDateSelect';
import ReportsFilter from '@/components/ReportsFilter';
import TagOptionsInput from '@/components/inputs/TagOptionsInput';
import periods from '@/domain/enums/periods';
import LoadingMixin from '@/services/loadingMixin';
import UserService from '@/domain/services/user';
import ReportFooterButtons from '@/app/generalPanel/reports/ReportFooterButtons';
import operationsHistory from '@/domain/data/generalPanel/reports/operationsHistory/operationTypeLookup';
import SaveTemplateModal from '@/domain/components/generalPanel/reports/operationsHistory/newReport/summary/SaveTemplateModal';
import OperationHistoryService from '@/domain/services/generalPanel/reports/operationHistory';
import { mapArrayIds, grammaticalJoin } from '@/services/arrayUtils';
import {
  formatter as dateTimeFormatter,
  joinISODateAndTimeString,
  setToStartOfDay,
  showDiff,
} from '@/services/dateTime/datetimeService';
import { formats, timezones } from '@/constants/localizer';
import OperationHistoryReportMixin from '@/domain/mixins/generalPanel/reports/operationsHistoryMixin';
import GenerateReportMixin from '@/domain/mixins/generalPanel/reports/generateReportMixin';
import { mapGetters } from 'vuex';

export default {
  components: {
    TagOptionsInput,
    PeriodDateSelect,
    ReportsFilter,
    ReportFooterButtons,
    SaveTemplateModal,
  },
  mixins: [
    LoadingMixin,
    GenerateReportMixin,
    OperationHistoryReportMixin,
  ],
  data() {
    return {
      reportsFilter: {
        period: periods.SEVEN,
        operationTypes: [],
        companiesIds: [],
        regionalIds: [],
        installationsIds: [],
        assetsIds: [],
        usersIds: [],
        genericSearch: '',
        finalDate: '',
        initialTime: '',
        finalTime: '',
        initialDate: '',
      },
      reportsFilterObjects: {
        companies: [],
        regional: [],
        installations: [],
        assets: [],
      },
      requiredFields: [
        'companiesIds',
        'installationsIds',
        'assetsIds',
      ],
      pagedFilter: {
        pageSize: 15,
        page: 1,
        sort: { dateTime: 'asc' },
        filter: {},
      },
      name: '',
      operationTypes: [],
      users: [],
      loadingUsers: false,
      invalidReportRange: false,
      saveTemplateLoading: false,
      exportXlsxLoading: false,
      showModal: false,
      emptyFieldsArray: [],
      originalTemplateName: null,
    };
  },
  computed: {
    ...mapGetters({
      limitHistoricalPeriodDataInDays: 'limitHistoricalPeriodDataInDays',
    }),
    operationTypesMap() {
      return mapArrayIds(this.reportsFilter.operationTypes);
    },
    isPeriodsUndefined() {
      return (
        this.reportsFilter.period === periods.PERSONALIZED
        || this.reportsFilter.period === undefined
      );
    },
    invalidReportPeriod() {
      const reportFilter = this.reportsFilter;
      if (reportFilter.period !== 0) {
        return false;
      }

      if (reportFilter.initialDate === null || reportFilter.finalDate === null) {
        return true;
      }

      return this.invalidReportRange;
    },
    isDateFilled() {
      const {
        initialDate,
        finalDate,
        initialTime,
        finalTime,
      } = this.reportsFilter;
      return initialDate && finalDate && initialTime && finalTime;
    },
    isPeriodValid() {
      return !(this.reportsFilter.period || {}).invalid
        && (this.isDateFilled || this.reportsFilter.period !== periods.PERSONALIZED);
    },
    usersMap() {
      return mapArrayIds(this.reportsFilter.usersIds);
    },
    emptyFields() {
      const {
        assetsIds,
        companiesIds,
        installationsIds,
        operationTypes,
      } = this.reportsFilter;
      return !(
        Array.isArray(assetsIds) && assetsIds.length > 0
          && Array.isArray(companiesIds) && companiesIds.length > 0
          && Array.isArray(installationsIds) && installationsIds.length > 0
          && Array.isArray(operationTypes) && operationTypes.length > 0
      );
    },
    haveUsers() {
      return this.users.length > 0;
    },
    templateId() {
      return this.$route?.params?.relatorioId || this.templateIdResponse;
    },
    isPeriodRangeValid() {
      if (!this.isDateFilled) return true;

      const initialDate = joinISODateAndTimeString(
        this.reportsFilter.initialDate,
        this.reportsFilter.initialTime,
        { timeZone: timezones.UTC },
      );
      const finalDate = joinISODateAndTimeString(
        this.reportsFilter.finalDate,
        this.reportsFilter.finalTime,
        { timeZone: timezones.UTC },
      );
      const diffDate = showDiff(finalDate, initialDate, 'days');
      return diffDate.days <= this.limitHistoricalPeriodDataInDays;
    },
  },
  created() {
    this.defineOperationTypes();
    this.getUsersUnderAdministration();
    this.getTemplateIfEdit();
  },
  methods: {
    getTemplateIfEdit() {
      if (!this.templateId) return;
      this.startLoading();
      OperationHistoryService.getTemplateById(this.templateId)
        .then((response) => {
          if (response.partial) {
            this.$pushAlert({ code: 'MI106' });
          }
          this.setFilterByTemplate(response);
        })
        .finally(() => {
          this.endLoading();
        });
    },
    setFilterByTemplate(response) {
      this.originalTemplateName = response.name;
      if (response.template.period === periods.PERSONALIZED) {
        this.onUpdateInitialDateTime(response.template.initialDateTime);
        this.onUpdateFinalDateTime(response.template.finalDateTime);
      }
      this.name = response.name;
      this.reportsFilter.genericSearch = response.template.genericSearch || '';
      this.reportsFilter.companiesIds = mapArrayIds(response.template.companies);
      this.reportsFilter.regionalIds = mapArrayIds(response.template.regionals);
      this.reportsFilter.installationsIds = mapArrayIds(response.template.installations);
      this.reportsFilter.assetsIds = mapArrayIds(response.template.assets);
      this.reportsFilter.usersIds = response.template.users;
      this.reportsFilter.operationTypes = this.operationTypes
        .filter((type) => response.template.operationTypes.includes(type.id));

      this.reportsFilter.period = response.template.period;
      this.reportsFilterObjects.companies = response.template.companies;
      this.reportsFilterObjects.regional = response.template.regionals;
      this.reportsFilterObjects.installations = response.template.installations;
      this.reportsFilterObjects.assets = response.template.assets;

      this.reportsFilter = {
        ...this.reportsFilter,
        ...this.splitDateTimes(response.template),
      };
      this.pagedFilter.filter = this.getReportsFilter();
    },
    splitDateTimes(template) {
      return {
        initialDate: setToStartOfDay(template.initialDateTime),
        initialTime: dateTimeFormatter(
          template.initialDateTime,
          formats.ISO_TIME,
        ),
        finalDate: setToStartOfDay(template.finalDateTime),
        finalTime: dateTimeFormatter(template.finalDateTime, formats.ISO_TIME),
      };
    },
    getReportsFilter() {
      return {
        ...this.reportsFilter,
        operationTypes: this.operationTypesMap,
        users: this.usersMap,
        usersIds: this.usersMap,
      };
    },
    exportXlsx() {
      if (this.invalidReportPeriod) {
        return;
      }

      this.startLoading();
      this.pagedFilter.filter = this.getReportsFilter();
      OperationHistoryService.exportHistoryExcel(this.pagedFilter)
        .then(() => this.$pushAlert({ code: 'MS011' }))
        .finally(() => this.endLoading());
    },
    openReport() {
      this.pagedFilter.filter = this.getReportsFilter();
      this.openOperationHistoryReport(this.pagedFilter);
    },
    defineOperationTypes() {
      this.operationTypes = operationsHistory(this);
      this.reportsFilter.operationTypes = this.operationTypes;
    },
    getUsersUnderAdministration() {
      this.loadingUsers = true;
      UserService.getUsersUnderAdministration()
        .then((data) => {
          this.users = data;
        })
      .finally(() => {
        this.loadingUsers = false;
      });
    },
    onUpdateInitialDateTime(value) {
      this.reportsFilter.initialDateTime = value;
    },
    onUpdateFinalDateTime(value) {
      this.reportsFilter.finalDateTime = value;
    },
    validateDateRange() {
      const initialDate = joinISODateAndTimeString(
        this.reportsFilter.initialDate,
        this.reportsFilter.initialTime,
        { timeZone: timezones.UTC },
      );
      const finalDate = joinISODateAndTimeString(
        this.reportsFilter.finalDate,
        this.reportsFilter.finalTime,
        { timeZone: timezones.UTC },
      );
      const diffDate = showDiff(finalDate, initialDate, 'days');
      if (diffDate.days > this.limitHistoricalPeriodDataInDays) {
        this.$pushAlert({
          code: 'ME091',
          messageParams: {
            0: this.limitHistoricalPeriodDataInDays,
          },
        });
      }
    },
    mountTemplateParams(transient, name) {
      this.reportsFilter.transient = transient;

      this.reportsFilter.initialDateTime = joinISODateAndTimeString(
        this.reportsFilter.initialDate,
        this.reportsFilter.initialTime,
      );
      this.reportsFilter.finalDateTime = joinISODateAndTimeString(
        this.reportsFilter.finalDate,
        this.reportsFilter.finalTime,
      );
      const operationTypes = this.reportsFilter.operationTypes.map((op) => op.id);

      return {
        name,
        template: {
          ...this.reportsFilter,
          usersIds: mapArrayIds(this.reportsFilter.usersIds),
          operationTypes,
          page: 1,
          pageSize: 10,
        },
      };
    },
    insertTemplate(transient = true, templateName = '') {
      const params = this.mountTemplateParams(transient, templateName);
      return OperationHistoryService.insertTemplate(params);
    },
    editTemplate(templateId, templateName) {
      const params = this.mountTemplateParams(undefined, templateName);
      return OperationHistoryService.updateTemplate(templateId, params);
    },
    saveTemplate() {
      this.saveTemplateLoading = true;
      return this.insertTemplate(false, this.name);
    },
    saveEditedTemplate() {
      return this.editTemplate(this.templateId, this.name);
    },
    saveHandler() {
      const callFunction = this.templateId
        ? this.saveEditedTemplate
        : this.saveTemplate;

      this.saveTemplateLoading = true;
      callFunction()
        .then(() => {
          this.$pushAlert({ code: 'MS006' });
          this.closeModal();
          this.backToList();
        })
        .finally(() => {
          this.saveTemplateLoading = false;
        });
    },
    showPeriodValidateMessage() {
      this.$pushAlert({
        code: 'MA001',
        messageParams: {
          NomeDoCampo: grammaticalJoin(
            this.emptyFieldsArray.map(field => this.getFieldLabel(field)),
          ),
        },
      });
    },
    getFieldLabel(field) {
      const fieldsMap = {
        companiesIds: this.$t('entities.company'),
        installationsIds: this.$t('entities.installation'),
        assetsIds: this.$t('entities.asset'),
        finalDate: this.$t('fields.general.final-date'),
        finalTime: this.$t('fields.general.final-time'),
        initialTime: this.$t('fields.general.initial-time'),
        initialDate: this.$t('fields.general.initial-date'),
      };
      return fieldsMap[field];
    },
    validatePeriodFields(value, field) {
      if (value) {
        this.emptyFieldsArray = this.emptyFieldsArray.filter(item => item !== field);
      }
      if ((this.reportsFilter.period !== undefined && this.isPeriodsUndefined)
        && !value && !this.emptyFieldsArray.includes(field)) {
        this.emptyFieldsArray.push(field);
        this.emptyFieldsArray = uniq(this.emptyFieldsArray);
        this.showPeriodValidateMessage();
        return;
      }

      if (this.isDateFilled) {
        this.validateDateRange();
      }
    },
    backToList() {
      return this.$router.push({
        name: 'reports',
        params: {
          activeTabName: 'operations-history',
        },
      });
    },
    openModal() {
      this.showModal = true;
    },
    closeModal() {
      this.onTemplateModalInput(false);
    },
    onTemplateModalInput(value) {
      this.showModal = value;

      if (!value && !this.saveTemplateLoading) {
        this.name = this.templateId ? this.originalTemplateName : '';
      }
    },
    cancel() {
      this.$confirmMessage({ code: 'MC006' }).accepted(() => {
        this.$router.push({ name: 'reports.operations-history' });
      });
    },
  },
};
</script>
<style lang="sass" scoped>
  .operations-history
    &__period-select
      margin-top: 15px
    &__buttons
      display: flex
      justify-content: flex-end
    &__xls-loading
      display: inline-flex
</style>
